// The instanced segment batch: every line the map draws (the plan's A.4).
//
// A line is one instance of a capsule or a box signed-distance field, never
// a tessellated polyline. A dashed order line is therefore ONE instance and
// not N, because the dash pattern is evaluated in the fragment shader.
//
// **The hairline rule** (the plan's A.4): the rasterised width is clamped to
// at least one device px and the colour is scaled by `min(1, true device
// width)`. A 1.8 px border at zoom 0.18 on DPR 2 is 0.65 device px wide; it
// draws one px wide at 65 % of its alpha, so it fades instead of sparkling.
// That is what the MSAA cache bought the PixiJS client
// (ui-map-interface.md §10.6).

import type { AttribSpec, BatchDeps } from './types';
import { StrokeBatch, STROKE_UNIFORMS } from './batch';
import { CORNER_ATTRIB, CORNER_DECODE_GLSL } from '../gl/corner';
import type { ProgramSource } from '../shaders/program';
import { BATCH_IDENTITY_GLSL, STROKE_ADD_GLSL } from '../shaders/addglow';
import { segmentModuleWgsl } from '../shaders/wgsl/segments';

/** 12 floats, 48 bytes. */
export const SEGMENT_FLOATS = 12;

// Location 0 is the per-vertex corner of `corner.ts`, so an instance
// attribute starts at 1 (R553).
export const SEGMENT_ATTRIBS: readonly AttribSpec[] = [
  { name: 'aP0', location: 1, size: 2, offset: 0 },
  { name: 'aP1', location: 2, size: 2, offset: 2 },
  { name: 'aWidth', location: 3, size: 1, offset: 4 },    // world px
  { name: 'aFlags', location: 4, size: 1, offset: 5 },
  { name: 'aColor', location: 5, size: 4, offset: 6 },    // straight rgba
  { name: 'aDash', location: 6, size: 2, offset: 10 },    // on/off world px, or the cut slopes of F_JOIN0/F_JOIN1
];

/** Writes one segment instance. Pure, so a test pins the layout. */
export function writeSegment(
  out: Float32Array, at: number,
  x0: number, y0: number, x1: number, y1: number,
  width: number, flags: number, color: number, alpha: number,
  dashOn: number, dashOff: number,
): void {
  out[at] = x0; out[at + 1] = y0;
  out[at + 2] = x1; out[at + 3] = y1;
  out[at + 4] = width; out[at + 5] = flags;
  out[at + 6] = ((color >> 16) & 0xff) / 255;
  out[at + 7] = ((color >> 8) & 0xff) / 255;
  out[at + 8] = (color & 0xff) / 255;
  out[at + 9] = alpha;
  out[at + 10] = dashOn; out[at + 11] = dashOff;
}

export const SEGMENT_VERTEX = `#version 300 es
layout(location=0) in float aCorner;
layout(location=1) in vec2 aP0;
layout(location=2) in vec2 aP1;
layout(location=3) in float aWidth;
layout(location=4) in float aFlags;
layout(location=5) in vec4 aColor;
layout(location=6) in vec2 aDash;
uniform mat3 uViewProj;
uniform float uZoom;
uniform float uPxPerWorld;
${BATCH_IDENTITY_GLSL}out vec2 vLocal;
out float vLen;
out float vHalf;
out float vRound;
out vec2 vDash;
out vec2 vJoin;
out vec2 vCut;
out vec4 vColor;
void main() {
${CORNER_DECODE_GLSL}
  int flags = int(aFlags + 0.5);
  float k = ((flags & 1) != 0) ? 1.0 / uZoom : 1.0;     // F_SCREEN
  float px = 1.0 / max(uPxPerWorld, 1e-6);              // one device px in world px
  float wWorld = aWidth * k;
  // half is a reserved word in GLSL ES, so the name stays halfW.
  float halfW = max(wWorld, px) * 0.5;
  bool rounded = (flags & 4) != 0;                       // F_ROUND
  vec2 d = aP1 - aP0;
  float len = length(d);
  vec2 t = len > 1e-6 ? d / len : vec2(1.0, 0.0);
  vec2 n = vec2(-t.y, t.x);
  // F_JOIN0 / F_JOIN1 (R3191): a joined end reaches past its point to the
  // outer point of the corner, at most three half widths along the line.
  vec2 join = vec2((flags & 8) != 0 ? 1.0 : 0.0, (flags & 16) != 0 ? 1.0 : 0.0);
  vec2 cut = aDash * join;
  float capExt = (rounded ? halfW : 0.0) + px;
  float along = mix(-capExt - min(abs(cut.x), 3.0) * halfW, len + capExt + min(abs(cut.y), 3.0) * halfW, corner.x);
  float across = mix(-(halfW + px), halfW + px, corner.y);
  vec2 world = aP0 + t * along + n * across;
  gl_Position = vec4((uViewProj * vec3(world, 1.0)).xy, 0.0, 1.0);
  vLocal = vec2(along, across);
  vLen = len;
  vHalf = halfW;
  vRound = rounded ? 1.0 : 0.0;
  vDash = (join.x + join.y) > 0.0 ? vec2(0.0) : aDash * k;
  vJoin = join;
  vCut = cut;
  float fade = min(1.0, wWorld * uPxPerWorld);           // the hairline rule
${STROKE_ADD_GLSL}
}`;

export const SEGMENT_FRAGMENT = `#version 300 es
precision highp float;
uniform float uAlpha;
in vec2 vLocal;
in float vLen;
in float vHalf;
in float vRound;
in vec2 vDash;
in vec2 vJoin;
in vec2 vCut;
in vec4 vColor;
out vec4 o;
void main() {
  // The distance along the line, SIGNED: negative inside the span, positive
  // past an end. The box branch must keep the sign. A value clamped at zero
  // makes d exactly zero over the whole interior, smoothstep(-w, w, 0.0)
  // returns 0.5, and a butt-capped line then draws at HALF its alpha and
  // steps from 0.5 to 0 at its ends. With the sign, the interior falls below
  // zero and reaches full coverage, and each end ramps like the two sides.
  // The round branch keeps the clamp, because length() cannot read a
  // negative x-distance: inside the span the cap distance is the across
  // distance alone.
  //
  // A joined end (F_JOIN0 / F_JOIN1, R3191) takes no butt edge. Its corner
  // stops at twice the half width along the bisector (the miter limit of
  // offsetLine), and the cut on the bisector is hard, past the smoothstep.
  float e0 = -vLocal.x, e1 = vLocal.x - vLen;
  float dxr = max(vJoin.x > 0.5 ? -1e9 : e0, vJoin.y > 0.5 ? -1e9 : e1);
  float b0 = -sign(vCut.x) * (vLocal.x * vCut.x + vLocal.y) * inversesqrt(1.0 + vCut.x * vCut.x) - 2.0 * vHalf;
  float b1 = sign(vCut.y) * (e1 * vCut.y + vLocal.y) * inversesqrt(1.0 + vCut.y * vCut.y) - 2.0 * vHalf;
  float d = vRound > 0.5
    ? length(vec2(max(dxr, 0.0), vLocal.y)) - vHalf
    : max(max(abs(vLocal.y) - vHalf, dxr), max(b0, b1));
  float w = max(fwidth(d), 1e-6);
  float cov = 1.0 - smoothstep(-w, w, d);
  cov *= step(vJoin.x * (vCut.x * vLocal.y + e0), 0.0) * step(vJoin.y * (e1 - vCut.y * vLocal.y), 0.0);
  if (vDash.x > 0.0) {
    float period = vDash.x + vDash.y;
    cov *= step(mod(vLocal.x, period), vDash.x);
  }
  o = vColor * cov * uAlpha;
}`;

/** The batch's program, as both backends read it. */
export function segmentProgram(): ProgramSource {
  const attribs = [CORNER_ATTRIB, ...SEGMENT_ATTRIBS];
  return {
    name: 'segments',
    glsl: { vertex: SEGMENT_VERTEX, fragment: SEGMENT_FRAGMENT },
    wgsl: segmentModuleWgsl(STROKE_UNIFORMS, attribs),
    uniforms: STROKE_UNIFORMS,
    attribs,
  };
}

export class SegmentBatch extends StrokeBatch {
  constructor(deps: BatchDeps, capacity = 256) {
    super(deps, 'segments', SEGMENT_FLOATS, SEGMENT_ATTRIBS, capacity);
  }

  /** One line. `dashOn <= 0` is solid. */
  line(
    x0: number, y0: number, x1: number, y1: number,
    width: number, color: number, alpha: number,
    flags = 0, dashOn = 0, dashOff = 0,
  ): void {
    const at = this.buf.reserve();
    writeSegment(this.buf.data, at, x0, y0, x1, y1, width, flags, color, alpha, dashOn, dashOff);
  }
}
